if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            if (this.classList.contains('quick-add-modal--upsell')) this.prepareUpsellQuickView(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            if (this.classList.contains('quick-add-modal--upsell')) this.initUpsellGallery();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      prepareUpsellQuickView(productElement) {
        productElement.classList.add('quick-add-product--upsell');

        const product = productElement.querySelector('.product');
        const mediaWrapper = productElement.querySelector('.product__media-wrapper');
        const mediaItems = this.getUpsellQuickViewMedia(productElement);
        if (!product || !mediaWrapper || !mediaItems.length) return;

        mediaWrapper.innerHTML = '';
        mediaWrapper.appendChild(this.createUpsellGallery(mediaItems));
        mediaWrapper.classList.add('quick-view-gallery-wrapper');
        product.classList.add('quick-view-product');
      }

      getUpsellQuickViewMedia(productElement) {
        const selectors = [
          '[data-device="desktop"] [data-gallery-set][data-variant-id="default"]',
          '[data-device="mobile"] [data-gallery-set][data-variant-id="default"]',
          '.js-hide-product-images',
          'product-gallery',
          '.product__media-wrapper',
        ];
        const seen = new Set();

        for (const selector of selectors) {
          const galleryRoot = productElement.querySelector(selector);
          if (!galleryRoot) continue;

          const media = Array.from(galleryRoot.querySelectorAll('.product__media img, deferred-media img'))
            .map((img) => ({
              src: this.getLargestImageSource(img),
              alt: img.getAttribute('alt') || productElement.querySelector('.product__title h1')?.textContent?.trim() || '',
            }))
            .filter((item) => item.src)
            .filter((item) => {
              const key = item.src.split('?')[0];
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });

          if (media.length) return media;
        }

        return [];
      }

      getLargestImageSource(img) {
        const srcset = img.getAttribute('srcset');
        if (srcset) {
          const candidates = srcset
            .split(',')
            .map((candidate) => {
              const [url, width = '0w'] = candidate.trim().split(/\s+/);
              return { url, width: Number(width.replace('w', '')) || 0 };
            })
            .filter((candidate) => candidate.url);

          if (candidates.length) {
            candidates.sort((a, b) => b.width - a.width);
            return candidates[0].url;
          }
        }

        return img.getAttribute('src');
      }

      createUpsellGallery(mediaItems) {
        const gallery = document.createElement('div');
        gallery.className = 'quick-view-gallery';
        gallery.setAttribute('data-quick-view-gallery', '');

        const main = document.createElement('div');
        main.className = 'quick-view-gallery__main';

        const mainImage = document.createElement('img');
        mainImage.className = 'quick-view-gallery__main-image';
        mainImage.src = mediaItems[0].src;
        mainImage.alt = mediaItems[0].alt;
        mainImage.loading = 'eager';
        mainImage.setAttribute('data-quick-view-main-image', '');
        main.appendChild(mainImage);

        const controls = document.createElement('div');
        controls.className = 'quick-view-gallery__controls';

        const previous = this.createUpsellGalleryButton('previous');
        const track = document.createElement('div');
        track.className = 'quick-view-gallery__thumbs';
        track.setAttribute('data-quick-view-thumbs', '');

        mediaItems.forEach((media, index) => {
          const thumb = document.createElement('button');
          thumb.className = 'quick-view-gallery__thumb';
          thumb.type = 'button';
          thumb.setAttribute('aria-label', `Show image ${index + 1}`);
          thumb.setAttribute('data-quick-view-thumb', String(index));

          const thumbImage = document.createElement('img');
          thumbImage.src = media.src;
          thumbImage.alt = media.alt;
          thumbImage.loading = 'lazy';
          thumb.appendChild(thumbImage);
          track.appendChild(thumb);
        });

        const next = this.createUpsellGalleryButton('next');

        controls.append(previous, track, next);
        gallery.append(main, controls);
        gallery._quickViewMediaItems = mediaItems;
        return gallery;
      }

      createUpsellGalleryButton(direction) {
        const button = document.createElement('button');
        button.className = `quick-view-gallery__nav quick-view-gallery__nav--${direction}`;
        button.type = 'button';
        button.setAttribute('aria-label', direction === 'previous' ? 'Previous image' : 'Next image');
        button.setAttribute('data-quick-view-gallery-nav', direction);
        button.innerHTML =
          direction === 'previous'
            ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 4 7 12l8 8" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>'
            : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 4 8 8-8 8" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>';
        return button;
      }

      initUpsellGallery() {
        const gallery = this.modalContent.querySelector('[data-quick-view-gallery]');
        if (!gallery || gallery._quickViewGalleryReady) return;

        const mainImage = gallery.querySelector('[data-quick-view-main-image]');
        const thumbs = Array.from(gallery.querySelectorAll('[data-quick-view-thumb]'));
        const mediaItems =
          gallery._quickViewMediaItems ||
          thumbs
            .map((thumb) => {
              const image = thumb.querySelector('img');
              return image ? { src: image.getAttribute('src'), alt: image.getAttribute('alt') || '' } : null;
            })
            .filter(Boolean);
        const navButtons = Array.from(gallery.querySelectorAll('[data-quick-view-gallery-nav]'));
        let activeIndex = 0;

        const setActive = (index) => {
          if (!mediaItems.length || !mainImage) return;
          activeIndex = (index + mediaItems.length) % mediaItems.length;
          mainImage.src = mediaItems[activeIndex].src;
          mainImage.alt = mediaItems[activeIndex].alt;

          thumbs.forEach((thumb, thumbIndex) => {
            const isActive = thumbIndex === activeIndex;
            thumb.classList.toggle('is-active', isActive);
            thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
          });

          thumbs[activeIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        };

        thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => setActive(Number(thumb.dataset.quickViewThumb)));
        });

        navButtons.forEach((button) => {
          button.addEventListener('click', () => {
            setActive(button.dataset.quickViewGalleryNav === 'previous' ? activeIndex - 1 : activeIndex + 1);
          });
        });

        navButtons.forEach((button) => button.toggleAttribute('hidden', mediaItems.length < 2));
        setActive(0);
        gallery._quickViewGalleryReady = true;
      }
    }
  );
}
